import React from 'react';

import Constants from '../../../Constants';

import { ROUTE_MY_DATA_SETS } from 'js/constants/pathnames';
import { RESOURCE_TYPE_SINK } from 'js/constants/resourceTypes';

import DateUtils from '../../../lib/DateUtils';
import { bytesToString, formatCode, formatNumber } from 'js/lib/FormatUtils';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import { Link } from 'react-router-dom';
import RoutePrefixContext from '../../../../new/contexts/RoutePrefixContext';
import ConfigurationViewContainer from '../../elements/configViews/ConfigurationViewContainer';
import TagsEditor from '../../elements/tags/TagsEditor';
import TagsView from '../../elements/tags/TagsView';
import { JSONView } from 'new/elements/JSONView/JSONView';
import { SwitchableSection } from 'new/elements/SwitchableSection/SwitchableSection';
import { MetricsPanel } from '@modules/monitor/resource-triage/components/MetricsPanel';
import {
  getPresetDateRange,
  formatDateRangeForApi,
} from '@modules/monitor/resource-triage/components/MetricsTrendPanel';
import { shouldHideRecordsForResource } from 'js/lib/PipelineUtils';
import { CredentialWidgetFromProps } from 'new/elements/CredentialWidget';

class DataSinkOverviewTab extends React.Component {
  static contextType = RoutePrefixContext;

  constructor(props) {
    super(props);
    this.state = {
      metricsRange: getPresetDateRange('30d'),
    };
  }

  componentDidMount() {
    this._loadData();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data.id !== this.props.data.id) {
      this._loadData();
    }
  }

  _loadData = () => {
    this.props.loadDataSink(this.props.data.id);
    this._loadResourceMetricsAggregate();
    this.props.fetchResourceMetricsPerDay(
      this.props.data.id,
      RESOURCE_TYPE_SINK,
      formatDateRangeForApi(this.state.metricsRange),
    );
  };

  _loadResourceMetricsAggregate = () => {
    this.props.fetchResourceMetricsAggregate(this.props.data.id, RESOURCE_TYPE_SINK);
  };

  onMetricsDatesChanged = (dates) => {
    this.setState({
      metricsRange: { start: new Date(dates.start), end: new Date(dates.end) },
    });
    this.props.fetchResourceMetricsPerDay(
      this.props.data.id,
      RESOURCE_TYPE_SINK,
      formatDateRangeForApi(dates),
    );
  };

  render() {
    const {
      data,
      metrics,
      credentials,
      connectionCategory,
      resourceTypeFields,
      isDataMap,
      isWritingDataset,
      writingDataSet,
      dataMap,
      readOnly,
    } = this.props;

    let createdDate = DateUtils.convertDateStringToFormat(data.created_at);
    let lastUpdate = DateUtils.convertDateStringToFormat(data.updated_at);

    let aggregateMetrics =
      metrics && has(metrics, 'agg') && has(metrics.agg, 'metrics') ? metrics.agg.metrics : null;
    let metricsByDate =
      metrics && has(metrics, 'byDate') && !isEmpty(metrics.byDate) ? metrics.byDate : null;

    let totalVolume = '-',
      numRecords = '-',
      errorsMetrics = '-';

    if (!this.props.isLoading.aggMetrics) {
      totalVolume = !isEmpty(aggregateMetrics)
        ? bytesToString(aggregateMetrics.size)
        : Constants.DATA_NOT_AVAILABLE;

      numRecords = !isEmpty(aggregateMetrics)
        ? formatNumber(aggregateMetrics.records)
        : Constants.DATA_NOT_AVAILABLE;

      errorsMetrics = !isEmpty(aggregateMetrics)
        ? formatNumber(aggregateMetrics.errors)
        : Constants.DATA_NOT_AVAILABLE;
    }

    // check if we hide records for this flow type
    const hideRecords = shouldHideRecordsForResource(data);

    const lifetimeMetrics = {
      records: numRecords,
      volume: totalVolume,
      errors: errorsMetrics,
    };

    let writingDataSetContent;

    const isLLM = data.node_type === 'llm_processor';

    if (isLLM) {
      writingDataSetContent = null;
    } else if (isWritingDataset === true) {
      const writingDataSetName = writingDataSet ? writingDataSet.name : null;

      writingDataSetContent = writingDataSetName ? (
        <>
          <div className="detail-key">Writing Nexset</div>
          <div className="detail-value">
            <Link
              to={`${this.context}${ROUTE_MY_DATA_SETS}/${data.data_set_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {writingDataSetName}
            </Link>
          </div>
        </>
      ) : (
        <>
          <div className="detail-key">Writing Nexset ID</div>
          <div className="detail-value">
            <Link
              to={`${this.context}${ROUTE_MY_DATA_SETS}/${data.data_set_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {data.data_set_id}
            </Link>
          </div>
        </>
      );
    } else {
      writingDataSetContent = (
        <>
          <div className="detail-key">Writing Nexset ID</div>
          <div className="detail-value">Not configured to write a Nexset</div>
        </>
      );
    }

    const hasCredentials = Boolean(credentials);
    let credentialsContent = credentials ? (
      <CredentialWidgetFromProps credential={credentials} />
    ) : null;

    const tagsView =
      readOnly === true ? (
        <TagsView data={this.props.data} />
      ) : (
        <TagsEditor id={data.id} resourceType={RESOURCE_TYPE_SINK} />
      );

    const dataMapElement = (
      <>
        {isDataMap || hasCredentials ? (
          <div className="detail-key">{isDataMap ? 'Lookup ID ' : 'Credentials'}</div>
        ) : null}

        {isDataMap || hasCredentials ? (
          <div className="detail-value">{isDataMap ? data.data_map : credentialsContent}</div>
        ) : null}
      </>
    );

    const createdOnElement = (
      <>
        <div className="detail-key">Created On</div>
        <div className="detail-value">{createdDate}</div>
      </>
    );

    const { sink_config, template_config = {} } = data;
    const configurations = Object.keys(template_config).length
      ? { sink_config, template_config }
      : { sink_config };
    const configurationsJSON = JSON.stringify(configurations);

    return (
      <div className="tab-content">
        <section>
          <div className="detail-row">
            <div className="detail-col">
              <div className="detail-key">{isLLM ? 'LLM Processor ID' : 'Destination ID'}</div>
              <div className="detail-value">{data.id}</div>
              {createdOnElement}
              {isDataMap ? dataMapElement : null}
              {hasCredentials && !isDataMap ? (
                <>
                  <div className="detail-key">Credentials</div>
                  <div className="detail-value">{credentialsContent}</div>
                </>
              ) : null}
            </div>

            <div className="detail-col">
              {writingDataSetContent}

              <div className="detail-key">Last Updated On</div>
              <div className="detail-value">{lastUpdate}</div>
            </div>
          </div>
        </section>

        <hr />

        <section>
          <SwitchableSection
            switchTitle="JSON view"
            title="Destination Configuration"
            DefaultComponent={
              <ConfigurationViewContainer
                entityType={RESOURCE_TYPE_SINK}
                connectionCategory={connectionCategory}
                config={data.sink_config}
                configFields={resourceTypeFields}
                resourceId={data.id}
                data={data}
                dataMap={dataMap}
              />
            }
            SwitchableComponent={
              <div style={{ height: 400 }}>
                <JSONView json={formatCode(configurationsJSON, 'application/json')} />
              </div>
            }
          />
        </section>

        <hr />

        <section>
          <div className="detail-row  single-col">
            <div className="detail-col">
              <div className="detail-key detail-tags-label">Tags</div>
              <div className="detail-value">{tagsView}</div>
            </div>
          </div>
        </section>

        {isLLM ? null : (
          <>
            <hr />

            <section>
              <MetricsPanel
                lifetimeMetrics={lifetimeMetrics}
                trendData={metricsByDate}
                start={this.state.metricsRange.start}
                end={this.state.metricsRange.end}
                onDatesChanged={this.onMetricsDatesChanged}
                onRefresh={this._loadResourceMetricsAggregate}
                hideRecords={hideRecords}
                loading={this.props.isLoading.aggMetrics || this.props.isLoading.dayMetrics}
              />
            </section>
          </>
        )}
      </div>
    );
  }
}

export default DataSinkOverviewTab;
